'use client';

import { useEffect, useState, useCallback } from 'react';

interface AdminSubmission {
  id: string;
  handle: string;
  score: number;
  foodTotal: number | null;
  foodCollected: number | null;
  submittedAt: string;
  totalSubs?: number;
}

interface SwarmAdminTabProps {
  onLoadCode: (code: string) => void;
}

export function SwarmAdminTab({ onLoadCode }: SwarmAdminTabProps) {
  const [ranked, setRanked] = useState<AdminSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [expandedHandle, setExpandedHandle] = useState<string | null>(null);
  const [handleSubs, setHandleSubs] = useState<Map<string, AdminSubmission[]>>(new Map());
  const [loadingHandle, setLoadingHandle] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/admin/submissions')
      .then(res => {
        if (!res.ok) throw new Error(res.status === 404 ? 'Admin API not available (dev only)' : 'Failed to fetch');
        return res.json();
      })
      .then(data => { setRanked(data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  const handleExpand = useCallback((handle: string) => {
    if (expandedHandle === handle) {
      setExpandedHandle(null);
      return;
    }
    setExpandedHandle(handle);
    if (!handleSubs.has(handle)) {
      setLoadingHandle(handle);
      fetch(`/api/admin/submissions?handle=${encodeURIComponent(handle)}`)
        .then(res => res.json())
        .then(data => {
          setHandleSubs(prev => new Map(prev).set(handle, data));
          setLoadingHandle(null);
        });
    }
  }, [expandedHandle, handleSubs]);

  const handleLoad = useCallback((sub: AdminSubmission) => {
    setActiveId(sub.id);
    fetch(`/api/admin/submissions?id=${sub.id}`)
      .then(res => res.json())
      .then(data => { if (data.code) onLoadCode(data.code); });
  }, [onLoadCode]);

  if (loading) return <div className="flex-1 flex items-center justify-center text-dim text-[11px]">Loading submissions...</div>;
  if (error) return <div className="flex-1 flex items-center justify-center text-red text-[11px]">{error}</div>;

  const totalSubs = ranked.reduce((s, r) => s + (r.totalSubs ?? 1), 0);

  return (
    <div className="flex-1 overflow-y-auto p-4 text-[11px] min-h-0">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-amber font-bold text-[10px] tracking-widest uppercase">
          All Submissions
        </h3>
        <span className="text-dim text-[9px]">{ranked.length} engineers, {totalSubs} total</span>
      </div>

      <div className="space-y-px">
        {ranked.map((entry, i) => {
          const isTop3 = i < 3;
          const isActive = activeId === entry.id;
          const isExpanded = expandedHandle === entry.handle;
          const subs = handleSubs.get(entry.handle);

          return (
            <div key={entry.id}>
              <div
                className={`flex items-center gap-2 px-2 py-1 rounded-sm hover:bg-elevated/50 transition-colors cursor-pointer ${
                  isActive ? 'bg-cyan/5' : ''
                }`}
                onClick={() => handleLoad(entry)}
              >
                <span className={`w-5 text-right ${isTop3 ? 'text-amber font-bold' : 'text-dim'}`}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className={`flex-1 truncate ${isActive ? 'text-cyan font-medium' : 'text-primary'}`}>
                  {entry.handle}
                  {(entry.totalSubs ?? 0) > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExpand(entry.handle);
                      }}
                      className="ml-1.5 px-1 py-0.5 rounded border border-stroke/50 text-dim hover:text-primary text-[9px] cursor-pointer transition-colors"
                    >
                      {entry.totalSubs}
                    </button>
                  )}
                </span>
                <span className={`w-12 text-right font-medium tabular-nums ${isTop3 ? 'text-amber' : 'text-primary'}`}>
                  {entry.score}
                </span>
              </div>

              {isExpanded && (
                <div className="ml-7 mb-1 space-y-px">
                  {loadingHandle === entry.handle ? (
                    <div className="px-2 py-0.5 text-dim text-[10px]">Loading...</div>
                  ) : subs?.map(sub => (
                    <div
                      key={sub.id}
                      className={`flex items-center gap-2 px-2 py-0.5 rounded-sm hover:bg-elevated/50 cursor-pointer text-[10px] ${
                        activeId === sub.id ? 'bg-cyan/5 text-cyan' : ''
                      }`}
                      onClick={() => handleLoad(sub)}
                    >
                      <span className="text-amber w-10 text-right tabular-nums">{sub.score}</span>
                      <span className="text-dim">{new Date(sub.submittedAt).toLocaleDateString()}</span>
                      <span className="text-dim">{new Date(sub.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
